import type { SystemStyleObject } from '@chakra-ui/react'

/**
 * Shared input field styles for all input types (text, number, select, textarea)
 * Used across NodeInput, NodeSelect, NodeTextarea, and direct Input components
 */
export const nodeInputFieldStyles: SystemStyleObject = {
  px: 2,
  py: 1.5,
  fontSize: 'sm',
  color: 'fg.default',
  bg: 'bg.panel',
  borderRadius: 'lg', // 8px
  borderWidth: '1px',
  borderColor: 'border.default',
  lineHeight: '1.5', // Ensure consistent line height
  _focus: {
    borderColor: 'blue.focusRing',
    borderWidth: '1px',
    outline: 'none',
    boxShadow: 'none',
  },
  _focusVisible: {
    outline: 'none',
    boxShadow: 'none',
  },
}

/**
 * Compact input field styles with reduced Y-axis padding
 * Used for option inputs in InteractiveWebhookNode, CallDistributionNode, and AutoAttendantNode
 */
export const nodeInputFieldStylesCompact: SystemStyleObject = {
  ...nodeInputFieldStyles,
  py: 0, // Reduced Y-axis padding
  h: '32px',
  borderRadius: 'lg', // 8px - ensure compact inputs also have consistent border radius
}

/**
 * Styles for select fields - matches the size of number/text inputs
 * Overrides Chakra's size="sm" defaults to match regular input sizing
 * Chevron indicator is positioned absolutely and doesn't require extra padding
 */
export const nodeSelectFieldStyles: SystemStyleObject = {
  ...nodeInputFieldStyles,
  px: 2, // Same horizontal padding as text/number inputs
  py: 1.5, // Same vertical padding as text/number inputs
  lineHeight: '1.5', // Same line height as text/number inputs
  height: 'auto', // Override size="sm" default height
  minHeight: 'auto', // Override size="sm" default minHeight
  overflow: 'hidden', // Ensure indicator doesn't extend beyond border
  position: 'relative', // Ensure field provides positioning context
}

/**
 * Shared input field styles with error state
 */
export const getNodeInputFieldStyles = (
  error?: boolean,
): SystemStyleObject => ({
  ...nodeInputFieldStyles,
  borderColor: error ? 'border.error' : 'border.default',
  _focus: {
    borderColor: error ? 'border.error' : 'blue.focusRing',
    borderWidth: '1px',
    outline: 'none',
    boxShadow: 'none',
  },
  _focusVisible: {
    outline: 'none',
    boxShadow: 'none',
  },
})

/**
 * Get styles for select fields with error state
 * Includes right padding for chevron indicator
 */
export const getNodeSelectFieldStyles = (
  error?: boolean,
): SystemStyleObject => ({
  ...nodeSelectFieldStyles,
  borderColor: error ? 'border.error' : 'border.default',
  _focus: {
    borderColor: error ? 'border.error' : 'blue.focusRing',
    borderWidth: '1px',
    outline: 'none',
    boxShadow: 'none',
  },
  _focusVisible: {
    outline: 'none',
    boxShadow: 'none',
  },
})

/**
 * Shared label styles for floating labels on inputs
 * Used across NodeInput, NodeSelect, NodeTextarea
 */
export const nodeInputLabelStyles: SystemStyleObject = {
  position: 'absolute',
  top: '-8px',
  left: '8px',
  fontSize: '11px',
  fontWeight: '500',
  color: 'fg.muted',
  bg: 'bg.panel',
  px: 1,
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
  zIndex: 1,
}

/**
 * Shared styles for select chevron indicator
 * Ensures chevron stays inside the field and doesn't overlap border or text
 */
export const nodeSelectIndicatorStyles: SystemStyleObject = {
  bg: 'bg.panel',
  position: 'absolute',
  right: '4px', // Position inside the field, closer to text
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'auto',
  height: 'auto',
  maxWidth: '16px',
  margin: 0,
  padding: 0,
  boxSizing: 'content-box',
}

/**
 * Shared styles for radio button controls
 * Note: SystemStyleObject doesn't support CSS selectors directly,
 * so we'll use a wrapper Box with css prop in the components
 */
export const nodeRadioControlStyles: SystemStyleObject = {
  // Base styles - these will be overridden by the css prop wrapper
}

/**
 * Shared button styles for "Add Option" buttons
 * Design spec: 4px border-radius, brand.muted border, brand.subtle bg, brand.fg text
 */
export const nodeAddOptionButtonStyles = {
  size: 'sm' as const,
  variant: 'outline' as const,
  colorPalette: 'blue' as const,
  color: 'brand.fg',
  bg: 'brand.subtle',
  borderColor: 'brand.muted',
  borderWidth: '1px',
  borderRadius: '4px',
  width: '100%',
  height: '36px',
  minWidth: '36px',
  px: '14px',
  py: '2px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  alignSelf: 'stretch',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: '600',
  lineHeight: '20px',
  textAlign: 'center' as const,
}
